import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useParams, useNavigate } from 'react-router-dom';
import { getPostDetail, ApiResponse } from '../../services/postApi';

// 게시글 상세 정보 인터페이스
interface PostDetailData {
  post_category: string;
  post_title: string;
  post_content: string;
  writer_name: string;
  writer_profile_image_url?: string;
  created_at: string;
  updated: boolean;
  can_update: boolean;
  attachment_urls: string[];
  reply_cnt: number;
  view_cnt: number;
  lecture_id?: number;
  lecture_title?: string;
  lecture_chapter?: string;
  study_name?: string;
  replies: ReplyData[];
}

// 댓글 데이터 인터페이스
interface ReplyData {
  reply_id: number;
  content: string;
  writer_name: string;
  writer_profile_image_url?: string;
  reply_created_at: string;
  images: string[];
  exposed: boolean;
}

const ArchiveDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // 상태 관리
  const [post, setPost] = useState<PostDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  // 날짜 포맷팅 함수
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const hours = date.getHours();
      const minutes = date.getMinutes();
      
      return `${year}년 ${month}월 ${day}일 ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    } catch (error) {
      /// console.error('날짜 파싱 오류:', error);
      return dateString;
    }
  };

  // HTML 콘텐츠에서 텍스트만 추출하는 함수
  const extractTextFromHTML = (html: string): string => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  // 게시글 상세 정보 조회
  const fetchPostDetail = async () => {
    if (!id) {
      setError('게시글 ID가 없습니다.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      // /// console.log(`아카이브 게시글 상세 조회 시작 - ID: ${id}`);
      
      const response: ApiResponse<PostDetailData> = await getPostDetail(parseInt(id));
      /// console.log('아카이브 게시글 상세 API 응답:', {id, response});
      
      if (response.code === 200) {
        // /// console.log('응답 코드 확인 통과 (200)');
        
        if (response.data) {
          // /// console.log('응답 데이터 존재 확인 통과');
          setPost(response.data);
          // /// console.log('게시글 상태 설정 완료:', response.data);
        } else {
          // /// console.log('응답 데이터가 없음');
          throw new Error('게시글 데이터가 없습니다.');
        }
      } else {
        /// console.log(`응답 코드 불일치 - 받은 코드: ${response.code}, 메시지: ${response.message}`);
        throw new Error(response.message || '게시글을 찾을 수 없습니다.');
      }
    } catch (error: any) {
      /// console.error('게시글 상세 조회 실패:', {error});
      setError(error.message || '게시글을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 컴포넌트 마운트시 데이터 로드
  useEffect(() => {
    fetchPostDetail();
  }, [id]);

  // 뒤로가기 핸들러
  const handleBackClick = () => {
    navigate('/archive');
  };

  // 재시도 핸들러
  const handleRetry = () => {
    fetchPostDetail();
  };

  // 로딩 상태
  if (loading) {
    return (
      <LayoutContainer>
        <Header />
        <MainContent>
          <LoadingContainer>
            <LoadingSpinner />
          </LoadingContainer>
        </MainContent>
        <Footer />
      </LayoutContainer>
    );
  }

  // 에러 상태
  if (error || !post) {
    return (
      <LayoutContainer>
        <Header />
        <MainContent>
          <ContentArea>
            <Container>
              <MainContainer>
                <ErrorContainer>
                  <ErrorMessage>{error || '게시글을 찾을 수 없습니다.'}</ErrorMessage>
                  <ActionButtons>
                    <RetryButton onClick={handleRetry}>다시 시도</RetryButton>
                    <BackButton onClick={handleBackClick}>목록으로</BackButton>
                  </ActionButtons>
                </ErrorContainer>
              </MainContainer>
            </Container>
          </ContentArea>
        </MainContent>
        <Footer />
      </LayoutContainer>
    );
  }

  return (
    <LayoutContainer>
      <Header />
      <MainContent>
        <ContentArea>
          <Container>
            <MainContainer>
              {/* 헤더 섹션 */}
              <HeaderSection>
                {/* 브레드크럼 */}
                <NavigationBar>
                  <BreadcrumbContainer>
                    <BreadcrumbItem>홈</BreadcrumbItem>
                    <ChevronIcon>
                      <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                        <path 
                          d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                          stroke="rgba(55, 56, 60, 0.61)" 
                          strokeWidth="1.5" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        />
                      </svg>
                    </ChevronIcon>
                    <BreadcrumbItem onClick={handleBackClick} style={{ cursor: 'pointer' }}>
                      아카이브
                    </BreadcrumbItem>
                  </BreadcrumbContainer>
                </NavigationBar>

                {/* 제목 */}
                <TitleSection>
                  <Title>{post.post_title}</Title>
                </TitleSection>

                {/* 작성자 정보 */}
                <AuthorSection>
                  <AuthorInfo>
                    <AuthorAvatar>
                      {post.writer_profile_image_url ? (
                        <img src={post.writer_profile_image_url} alt="작성자" />
                      ) : (
                        <img src="/images/default-avatar.png" alt="작성자" />
                      )}
                    </AuthorAvatar>
                    <AuthorName>{post.writer_name}</AuthorName>
                    <Divider />
                    <PostDate>{formatDate(post.created_at)}</PostDate>
                    {post.updated && <PostDate>수정됨</PostDate>}
                  </AuthorInfo>
                </AuthorSection>

                {/* 구분선 */}
                <HeaderDivider />
              </HeaderSection>

              {/* 본문 섹션 */}
              <ContentSection>
                <PostContent>
                  {/* HTML 콘텐츠를 안전하게 렌더링 */}
                  <ContentHTML 
                    dangerouslySetInnerHTML={{ __html: post.post_content }}
                  />

                  {/* 첨부파일 */}
                  {post.attachment_urls && post.attachment_urls.length > 0 && (
                    <AttachmentSection>
                      <AttachmentTitle>첨부파일</AttachmentTitle>
                      <AttachmentList>
                        {post.attachment_urls.map((url, index) => (
                          <AttachmentItem key={index}>
                            <AttachmentImage src={url} alt={`첨부파일 ${index + 1}`} />
                          </AttachmentItem>
                        ))}
                      </AttachmentList>
                    </AttachmentSection>
                  )}
                </PostContent>
              </ContentSection>

              {/* 댓글 섹션 */}
              {post.replies && post.replies.length > 0 && (
                <CommentSection>
                  <CommentTitle>댓글 {post.reply_cnt}개</CommentTitle>
                  <CommentList>
                    {post.replies
                      .filter(reply => reply.exposed)
                      .map((reply) => (
                        <CommentItem key={reply.reply_id}>
                          <CommentHeader>
                            <CommentAuthorInfo>
                              <CommentAuthorAvatar>
                                {reply.writer_profile_image_url ? (
                                  <img src={reply.writer_profile_image_url} alt="작성자" />
                                ) : (
                                  <img src="/images/default-avatar.png" alt="작성자" />
                                )}
                              </CommentAuthorAvatar>
                              <CommentAuthorName>{reply.writer_name}</CommentAuthorName>
                              <CommentDate>{formatDate(reply.reply_created_at)}</CommentDate>
                            </CommentAuthorInfo>
                          </CommentHeader>
                          
                          <CommentContent>
                            <CommentText 
                              dangerouslySetInnerHTML={{ __html: reply.content }}
                            />
                            
                            {/* 댓글 이미지 */}
                            {reply.images && reply.images.length > 0 && (
                              <CommentImages>
                                {reply.images.map((image, index) => (
                                  <CommentImage key={index} src={image} alt={`댓글 이미지 ${index + 1}`} />
                                ))}
                              </CommentImages>
                            )}
                          </CommentContent>
                        </CommentItem>
                      ))}
                  </CommentList>
                </CommentSection>
              )}
            </MainContainer>
          </Container>
        </ContentArea>
      </MainContent>
      <Footer />
    </LayoutContainer>
  );
};

// 스타일 컴포넌트들
const LayoutContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-width: 320px;
  overflow-x: hidden;
`;

const MainContent = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  position: relative;
  min-height: calc(100vh - 80px);
  align-items: flex-start;
  justify-content: center;
`;

const ContentArea = styled.main`
  width: 100%;
  min-height: calc(100vh - 80px);
  background: white;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

const Container = styled.div`
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
`;

const MainContainer = styled.div`
  width: 1200px;
  padding-top: 144px; /* 헤더 높이(80px) + 여유 공간(64px) */
  padding-bottom: 128px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 32px;

  @media (max-width: 1240px) {
    width: calc(100% - 40px);
    margin: 0 20px;
  }
`;

const LoadingContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 200px 20px;
`;

const ErrorContainer = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  padding: 80px 20px;
  background: #fef2f2;
  border-radius: 8px;
  border: 1px solid #fecaca;
`;

const ErrorMessage = styled.p`
  color: #dc2626;
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  text-align: center;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const RetryButton = styled.button`
  padding: 12px 24px;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #b91c1c;
  }
`;

const BackButton = styled.button`
  padding: 12px 24px;
  background: white;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #f9fafb;
  }
`;

const HeaderSection = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 24px;
`;

const NavigationBar = styled.div`
  align-self: stretch;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BreadcrumbContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const ChevronIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BreadcrumbItem = styled.span`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.09px;
  
  &:hover {
    color: var(--Primary-Normal, #448181);
  }
`;

const TitleSection = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
`;

const Title = styled.h1`
  color: var(--Label-Normal, #171719);
  font-size: 28px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 38.02px;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 24px;
    line-height: 32px;
  }
`;

const AuthorSection = styled.div`
  align-self: stretch;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const AuthorInfo = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const AuthorAvatar = styled.div`
  position: relative;
  border-radius: 10000px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  background: var(--Static-White, white);
  overflow: hidden;
  outline: 1px solid var(--Line-Normal-Alternative, rgba(112, 115, 124, 0.08));
  outline-offset: -1px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AuthorName = styled.span`
  color: var(--Label-Neutral, rgba(46, 47, 51, 0.88));
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const PostDate = styled.span`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const UpdatedTag = styled.span`
  padding: 2px 6px;
  background: #fef3c7;
  color: #d97706;
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  border-radius: 4px;
`;

const PostStats = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const PostStat = styled.span`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20.01px;
  letter-spacing: 0.20px;
`;

const Divider = styled.div`
  width: 1px;
  height: 12px;
  background: var(--Line-Normal-Normal, rgba(112, 115, 124, 0.22));
`;

const HeaderDivider = styled.div`
  align-self: stretch;
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  
  &::after {
    content: '';
    align-self: stretch;
    height: 1px;
    background: var(--Line-Normal-Normal, rgba(112, 115, 124, 0.22));
  }
`;

const ContentSection = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 32px;
`;

const PostContent = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 24px;
`;

const ContentHTML = styled.div`
  align-self: stretch;
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 0.09px;

  p {
    margin: 0 0 16px 0;
    &:last-child {
      margin-bottom: 0;
    }
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 16px 0;
  }

  ul, ol {
    padding-left: 20px;
    margin: 16px 0;
  }

  li {
    margin: 8px 0;
  }

  blockquote {
    border-left: 4px solid #e5e7eb;
    padding-left: 16px;
    margin: 16px 0;
    color: #6b7280;
  }

  pre {
    background: #f3f4f6;
    padding: 16px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 16px 0;
  }

  code {
    background: #f3f4f6;
    padding: 2px 4px;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
  }

  @media (max-width: 768px) {
    font-size: 15px;
    line-height: 24px;
  }
`;

const AttachmentSection = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const AttachmentTitle = styled.h3`
  color: var(--Label-Normal, #171719);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  margin: 0;
`;

const AttachmentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AttachmentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AttachmentImage = styled.img`
  max-width: 200px;
  max-height: 200px;
  border-radius: 8px;
  object-fit: cover;
`;

const CommentSection = styled.div`
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-top: 32px;
  border-top: 1px solid var(--Line-Normal-Normal, rgba(112, 115, 124, 0.22));
`;

const CommentTitle = styled.h2`
  color: var(--Label-Normal, #171719);
  font-size: 20px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  margin: 0;
`;

const CommentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const CommentItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CommentAuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CommentAuthorAvatar = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  overflow: hidden;
  background: white;
  border: 1px solid #e5e7eb;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CommentAuthorName = styled.span`
  color: var(--Label-Normal, #171719);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
`;

const CommentDate = styled.span`
  color: var(--Label-Alternative, rgba(55, 56, 60, 0.61));
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
`;

const CommentContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CommentText = styled.div`
  color: var(--Label-Normal, #171719);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20px;

  p {
    margin: 0 0 8px 0;
    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const CommentImages = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const CommentImage = styled.img`
  max-width: 120px;
  max-height: 120px;
  border-radius: 4px;
  object-fit: cover;
`;

export default ArchiveDetail;
